import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import useDeleteAssessmentDialog from '@app/src/hooks/useDeleteAssessmentDialog'
import { useOrganizationAssessmentSettingsCount } from '@app/src/hooks/useOrganizationAssessmentSettingsCount'
import SustainabilityLibraryCollection from '@app/src/pages/Configurations/SustainabilityLibraryPages/SustainabilityLibraryCollection'
import { SUSTAINABILITY_LIBRARY_PAGE_IDS } from '@app/src/pages/Configurations/SustainabilityLibraryScene'
import type { AssessmentTemplateWithClassificationRules } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { NotificationSeverity, ONE_MINUTE_MS } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import { DeleteOutlined, SyncOutlined } from '@mui/icons-material'
import type { RadioGroupProps } from '@mui/material'
import { CircularProgress, Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { generatePath } from 'react-router'
import AssessmentForm from './AssessmentForm'
import type { AssessmentTemplateFormValues } from './CreateAssessmentFromQuestionnaire'
import SyncAssessmentWithQuestionnaireDialog from './SyncAssessmentWithQuestionnaireDialog'

type EditAssessmentProps = {
  assessmentId: number
}

const EditAssessment: React.FC<EditAssessmentProps> = ({ assessmentId }) => {
  const { formatMessage } = useIntl()
  const formMethods = useForm<AssessmentTemplateFormValues>({ shouldUnregister: false })
  const [intervals, setIntervals] = useState<number>(5)
  const { mutate: updateAssessmentTemplate, isPending: isUpdating } = useCreateResource()
  const { showErrorNotification } = useErrorNotification()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const organizationAssessmentSettingsCount = useOrganizationAssessmentSettingsCount(assessmentId)
  const { confirmDelete } = useDeleteAssessmentDialog({ assessmentTemplateId: assessmentId })
  const [isSyncDialogOpen, openSyncDialog, closeSyncDialog] = useDialogState()
  const { isDirty } = formMethods.formState

  const {
    data: assessmentTemplateWithClassificationRules,
    isLoading,
    dataUpdatedAt,
  } = useFetchResource<AssessmentTemplateWithClassificationRules>({
    key: [FetchKey.AssessmentTemplateWithClassificationRules, assessmentId],
    endpoint: endpoints.assessmentTemplateWithClassificationRules(assessmentId),
    options: {
      staleTime: ONE_MINUTE_MS,
      enabled: Boolean(assessmentId),
    },
  })

  const assessmentTemplate = assessmentTemplateWithClassificationRules?.assessmentTemplate
  const questions = assessmentTemplateWithClassificationRules?.questions ?? []
  const assessmentSections = assessmentTemplateWithClassificationRules?.sections ?? []
  const hasUnsavedChanges = isDirty || (Boolean(assessmentTemplate) && intervals !== (assessmentTemplate?.levels ?? 5))

  const onSubmit = (values: AssessmentTemplateFormValues) => {
    if (!assessmentTemplate) return
    const { sections, ...assessmentTemplateValues } = values
    const assessmentTemplateToEdit = {
      ...assessmentTemplateValues,
      id: assessmentTemplate.id,
      questionnaireTemplateId: assessmentTemplate.questionnaireTemplateId,
      levels: intervals,
    }

    const questionIds =
      values.assessmentType === AssessmentType.Flexible && sections
        ? sections.flatMap(s => s.questions.map(q => q.questionId))
        : questions.map(q => q.id)

    let body
    if (values.assessmentType === AssessmentType.Flexible && sections) {
      body = {
        assessmentTemplate: assessmentTemplateToEdit,
        sections: sections.map((section, i) => ({
          id: section.id || null,
          name: assessmentTemplateValues[`section${i + 1}Name` as keyof typeof assessmentTemplateValues],
          questions: section.questions.map(question => ({
            questionId: question.questionId,
            veryPositivePoints: question.points?.veryPositive || null,
            positivePoints: question.points?.positive || null,
            negativePoints: question.points?.negative || null,
            negativeAdversePoints: question.points?.negativeAdverse || null,
          })),
        })),
        questionIds,
        questionnaireTemplateId: assessmentTemplate.questionnaireTemplateId,
      }
    } else {
      body = {
        assessmentTemplate: assessmentTemplateToEdit,
        questionIds,
        questionnaireTemplateId: assessmentTemplate.questionnaireTemplateId,
      }
    }

    updateAssessmentTemplate(
      {
        url: endpoints.assessmentTemplate,
        body,
      },
      {
        onError: error => {
          showErrorNotification({ requestError: error })
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationSettings] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentTemplateWithClassificationRules] })
          showSnackbar({
            message: formatMessage({ id: 'assessments.successfullyUpdated' }),
            severity: NotificationSeverity.success,
          })
        },
      },
    )
  }

  const handleIntervalsChange: RadioGroupProps['onChange'] = event => {
    setIntervals(parseInt(event.target.value))
  }

  useEffect(() => {
    if (assessmentTemplate) {
      setIntervals(assessmentTemplate.levels ?? 5)
      formMethods.reset({ ...assessmentTemplate })
    }
  }, [assessmentTemplate])

  return (
    <>
      <FormProvider {...formMethods}>
        <SustainabilityLibraryCollection
          title={formatMessage({ id: 'assessments.editAssessment' })}
          actionButtons={
            isLoading
              ? []
              : [
                  {
                    label: formatMessage({ id: 'general.delete' }),
                    onClick: confirmDelete,
                    variant: 'outlined',
                    startIcon: <DeleteOutlined />,
                    disabled: !!organizationAssessmentSettingsCount || isUpdating,
                    tooltip: organizationAssessmentSettingsCount
                      ? formatMessage({ id: 'assessmentTemplate.delete.toolTip' })
                      : undefined,
                  },
                  ...(assessmentTemplate?.assessmentType !== AssessmentType.BaselineAssessment &&
                  assessmentTemplate?.questionnaireTemplateId
                    ? [
                        {
                          label: formatMessage({ id: 'assessments.sync.buttonLabel' }),
                          onClick: openSyncDialog,
                          variant: 'outlined' as const,
                          startIcon: <SyncOutlined />,
                          disabled: isUpdating || hasUnsavedChanges,
                          tooltip: hasUnsavedChanges
                            ? formatMessage({ id: 'assessments.sync.unsavedChangesTooltip' })
                            : undefined,
                        },
                      ]
                    : []),
                  {
                    label: formatMessage({ id: 'general.cancel' }),
                    to: generatePath(paths.sustainabilityLibrary, {
                      configurationsPage: SUSTAINABILITY_LIBRARY_PAGE_IDS.Assessments,
                    }),
                    disabled: isUpdating,
                  },
                  {
                    label: formatMessage({ id: 'general.update' }),
                    onClick: formMethods.handleSubmit(onSubmit),
                    variant: 'contained',
                    disabled: isUpdating || assessmentTemplate?.assessmentType === AssessmentType.BaselineAssessment,
                    loading: isUpdating,
                  },
                ]
          }
        >
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" mt={8}>
              <CircularProgress />
            </Stack>
          ) : (
            <AssessmentForm
              key={dataUpdatedAt}
              intervals={intervals}
              onIntervalsChange={handleIntervalsChange}
              sections={assessmentSections}
              isPublished={assessmentTemplate?.isPublished}
              disablePublishButton={Boolean(organizationAssessmentSettingsCount)}
              disableAssessmentTypeChange
              assessmentTemplate={assessmentTemplate}
            />
          )}
        </SustainabilityLibraryCollection>
      </FormProvider>
      <SyncAssessmentWithQuestionnaireDialog
        assessmentTemplateId={assessmentId}
        assessmentType={assessmentTemplate?.assessmentType}
        open={isSyncDialogOpen}
        onClose={closeSyncDialog}
      />
    </>
  )
}

export default EditAssessment
